import {
  Clock,
  Play,
  Checked,
  Graph,
  Hexagon,
  MobileDevice,
  Book
} from 'assets/icons/revamp';
import Warning from 'assets/icons/Warning';
import { t } from 'services/i18n.service';
import { cloneElement, ReactElement, useMemo } from 'react';
import { IconProps } from 'interfaces';
import { useAppSelectorShallow } from 'store/hooks';
import { modalContentSelector } from 'store/reducer/modal/more-options.modal.selectors';
import { MORE_OPTION_REVAMP_TAB } from 'enums';
import {
  getMoreOptionsVisibleTabIds,
  MoreOptionTabId,
  getActivityVideoDurationWarning
} from 'utils/more-options.util';

export type TabId = MoreOptionTabId;

const tabs: Tab[] = [
  {
    id: MORE_OPTION_REVAMP_TAB.TIMING,
    label: 'tabs_revamp.timing_tab.timing',
    icon: <Clock />
  },
  {
    id: MORE_OPTION_REVAMP_TAB.VIDEOS,
    label: 'tabs_revamp.videos_tab.add_videos',
    icon: <Play />
  },
  {
    id: MORE_OPTION_REVAMP_TAB.ALLOWED_APPS,
    label: 'tabs_revamp.allowed_apps_tab.allow_apps',
    icon: <MobileDevice />
  },
  {
    id: MORE_OPTION_REVAMP_TAB.LOG_DATA,
    label: 'tabs_revamp.log_data_tab.log_data',
    icon: <Graph />
  },
  {
    id: MORE_OPTION_REVAMP_TAB.ACTIVITY_CHOICES,
    label: 'tabs_revamp.activity_choices_tab.sub_habits',
    icon: <Checked />
  },
  {
    id: MORE_OPTION_REVAMP_TAB.INSTRUCTIONS,
    label: 'tabs_revamp.instructions_tab.instructions',
    icon: <Book />
  },
  {
    id: MORE_OPTION_REVAMP_TAB.ADVANCED,
    label: 'tabs_revamp.advanced_tab.advanced',
    icon: <Hexagon />
  }
];

interface Tab {
  id: TabId;
  label: string;
  icon: React.ReactNode;
}

interface MoreOptionsProps {
  activeTab: TabId;
  onTabChange: (tabId: TabId) => void;
}

const MoreOptions = ({ activeTab, onTabChange }: MoreOptionsProps) => {
  const { activityType, platform, flags, activity, videos_metadata } =
    useAppSelectorShallow(modalContentSelector);

  const { showWarning: showVideoDurationWarning, videoDurationSeconds } =
    useMemo(
      () => getActivityVideoDurationWarning(activity, videos_metadata),
      [activity, videos_metadata]
    );

  const renderTabs = useMemo(() => {
    const visibleTabIds = getMoreOptionsVisibleTabIds({
      context: { activityType, platform, flags }
    });

    return tabs.filter((tab) => visibleTabIds.includes(tab.id));
  }, [activityType, flags, platform]);

  return (
    <div className='min-h-max flex items-center ~gap-1/2 border-b border-primary-50 dark:border-primary-900 bg-primary-100 dark:bg-primary-dark-50 ~p-1/2 flex-wrap overflow-y-hidden'>
      {renderTabs.map((tab) => {
        const isActive = activeTab === tab.id;
        return (
          <button
            key={tab.id}
            onClick={() => onTabChange(tab.id)}
            className={`flex items-center ~gap-1.5/2 ~px-3/4 ~py-1.5 relative transition-colors duration-200 whitespace-nowrap ${
              isActive
                ? 'text-primary-900 font-medium dark:text-primary-dark-600'
                : 'text-gray-600 dark:text-secondary-dark-900 hover:text-primary-800 dark:hover:text-primary-dark-600'
            }`}
            aria-selected={isActive}
            role='tab'>
            {cloneElement(tab.icon as ReactElement<IconProps>, {
              fill: 'currentColor',
              fillDarkMode: 'currentColor',
              className: '~w-3/4 ~h-3/4'
            })}

            <span className='~text-xs/sm flex items-center ~gap-1/1.5'>
              {t(tab.label)}
              {tab.id === MORE_OPTION_REVAMP_TAB.VIDEOS &&
                showVideoDurationWarning &&
                videoDurationSeconds && (
                  <span
                    className='inline-flex'
                    title={t('activity_duration_warning', {
                      video_duration: videoDurationSeconds,
                      activity_duration: activity?.duration_seconds
                    })}>
                    <Warning />
                  </span>
                )}
            </span>
          </button>
        );
      })}
    </div>
  );
};

export default MoreOptions;
